import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQueryIntegrations, useQueryAllPipelineTags } from '@ion/api';
import { alphabetizeBy } from '@ion/mrdash';
import { useToggle } from '@ion/components';
import { useLazyQueryIonPipelines } from 'ion-experimental/api/pipelines';

import buildRevisionOptionMaps from './buildRevisionOptionMaps';

const DEFAULT_INTEGRATION_OPTION = { value: '_ALL_', label: 'All Integrations' };
const DEFAULT_REVISION_OPTION = { value: '_ALL_', label: 'All Revisions' };
const DEFAULT_PER_PAGE = 20;
const PER_PAGE_OPTIONS = [10, 20, 50];

export default function useBulkPlaybookUpdateTableContextProps() {
  // Target playbook selection
  const [selectedIntegrationId, setSelectedIntegrationId] = useState('');
  const [selectedPlaybookId, setSelectedPlaybookId] = useState('');

  // Pipeline selection
  const [selectedPipelineIds, setSelectedPipelineIds] = useState(new Set());
  const [onlyShowSelected, setOnlyShowSelected] = useState(false);
  const [clearSelectedKey, toggleClearSelectedKey] = useToggle();

  // Filter state
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIntegrationId, setFilterIntegrationId] = useState(DEFAULT_INTEGRATION_OPTION.value);
  const [filterPlaybookId, setFilterPlaybookId] = useState(DEFAULT_REVISION_OPTION.value);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Pagination (client-side - table handles pagination, we just track perPage preference)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  // Sorting
  const [sort, setSort] = useState({ field: 'name', direction: 'asc' });

  // Fetch integrations for dropdowns
  // Note: useQueryIntegrations returns data as the integrations array directly
  const {
    data: integrations,
    loading: integrationsLoading,
    error: integrationsError,
  } = useQueryIntegrations({ variables: { limit: null } });

  // Build integration options for filter dropdowns (includes "All Integrations")
  const integrationOptions = useMemo(() => {
    const options = integrations.map(integration => ({
      value: integration.id,
      label: integration.name,
    }));
    return [DEFAULT_INTEGRATION_OPTION, ...alphabetizeBy(options, 'label')];
  }, [integrations]);

  // Build integration options for target dropdown (no "All" option)
  const targetIntegrationOptions = useMemo(() => {
    const options = integrations.map(integration => ({
      value: integration.id,
      label: integration.name,
    }));
    return alphabetizeBy(options, 'label');
  }, [integrations]);

  const { revisionsByIntegrationId, targetRevisionsByIntegrationId } = useMemo(
    () =>
      buildRevisionOptionMaps({
        integrations,
        defaultIntegrationOption: DEFAULT_INTEGRATION_OPTION,
        defaultRevisionOption: DEFAULT_REVISION_OPTION,
      }),
    [integrations]
  );

  // Fetch tags for filter dropdown
  const { data: allTags, loading: tagsLoading } = useQueryAllPipelineTags();
  const tagOptions = (allTags ?? []).map(tag => ({ value: tag.id, label: tag.label }));

  // Data fetching for pipelines
  const [fetchPipelines, { pipelines, pagination, loading }] = useLazyQueryIonPipelines();

  // Fetch all pipelines when filters change (client-side pagination)
  useEffect(() => {
    const integrationIds = filterIntegrationId === DEFAULT_INTEGRATION_OPTION.value ? [] : [filterIntegrationId];
    const playbookIds = filterPlaybookId === DEFAULT_REVISION_OPTION.value ? [] : [filterPlaybookId];

    fetchPipelines({
      clusterIds: selectedClusters,
      searchTerm: searchTerm || undefined,
      integrationIds,
      playbookIds,
      tagIds: selectedTagIds,
      sort,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClusters, searchTerm, filterIntegrationId, filterPlaybookId, selectedTagIds, sort]);

  // Reset target revision when target integration changes
  useEffect(() => {
    setSelectedPlaybookId(DEFAULT_REVISION_OPTION.value);
  }, [selectedIntegrationId]);

  // Reset filter revision when filter integration changes
  useEffect(() => {
    setFilterPlaybookId(DEFAULT_REVISION_OPTION.value);
  }, [filterIntegrationId]);

  // Selection handlers
  const handleSelectAll = useCallback(
    selected => {
      if (selected) {
        const newSet = new Set(selectedPipelineIds);
        pipelines.forEach(pipeline => newSet.add(pipeline.id));
        setSelectedPipelineIds(newSet);
      } else {
        const newSet = new Set(selectedPipelineIds);
        pipelines.forEach(pipeline => newSet.delete(pipeline.id));
        setSelectedPipelineIds(newSet);
      }
    },
    [pipelines, selectedPipelineIds]
  );

  const handleSelectPipeline = useCallback((pipelineId, selected) => {
    setSelectedPipelineIds(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(pipelineId);
      } else {
        newSet.delete(pipelineId);
      }
      return newSet;
    });
  }, []);

  const clearSelection = useCallback(() => {
    toggleClearSelectedKey();
    setSelectedPipelineIds(new Set());
  }, [toggleClearSelectedKey]);

  // Check if target playbook is selected
  const hasTargetPlaybook = useMemo(
    () =>
      selectedIntegrationId !== DEFAULT_INTEGRATION_OPTION.value &&
      selectedPlaybookId !== DEFAULT_REVISION_OPTION.value,
    [selectedIntegrationId, selectedPlaybookId]
  );

  const selectedCount = selectedPipelineIds.size;

  return {
    // Target playbook
    selectedIntegrationId,
    setSelectedIntegrationId,
    selectedPlaybookId,
    setSelectedPlaybookId,
    hasTargetPlaybook,

    // Pipeline selection
    selectedPipelineIds,
    setSelectedPipelineIds,
    selectedCount,
    handleSelectAll,
    handleSelectPipeline,
    clearSelection,
    clearSelectedKey,
    onlyShowSelected,
    setOnlyShowSelected,

    // Filters
    selectedClusters,
    setSelectedClusters,
    searchTerm,
    setSearchTerm,
    filterIntegrationId,
    setFilterIntegrationId,
    filterPlaybookId,
    setFilterPlaybookId,
    selectedTagIds,
    setSelectedTagIds,
    tagOptions,
    tagsLoading,

    // Pagination (client-side)
    perPage,
    setPerPage,
    perPageOptions: PER_PAGE_OPTIONS,

    // Sorting
    sort,
    setSort,

    // Pipeline data
    pipelines,
    pagination,
    loading,

    // Integration data
    integrations,
    integrationOptions,
    targetIntegrationOptions,
    revisionsByIntegrationId,
    targetRevisionsByIntegrationId,
    integrationsLoading,
    integrationsError,

    // Constants
    DEFAULT_INTEGRATION_OPTION,
    DEFAULT_REVISION_OPTION,
  };
}

export { DEFAULT_INTEGRATION_OPTION, DEFAULT_REVISION_OPTION };
